import React, { useCallback, useEffect, useRef, useState } from 'react';
import adminApi from '../api/client';

type ReportCase = {
  id: string; listing_id: string; reason: string; description: string | null; status: string;
  reporter: { id: string; name: string | null };
  snapshot: { title: string; description: string; price_lkr: number | null; photos: string[] } | null;
  current_listing: { title: string; status: string; admin_hidden: boolean } | null;
  seller: { id: string; name: string; is_suspended: boolean } | null;
  resolution_note: string | null; resolution_action: string | null;
};
type Action = 'dismiss_report' | 'remove_listing' | 'remove_and_suspend';
const errorText = (error: any, fallback: string) => {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : detail?.message || fallback;
};

export default function MarketplaceReports({ onListingChanged }: { onListingChanged?: () => void }) {
  const [rows, setRows] = useState<ReportCase[]>([]);
  const [status, setStatus] = useState('pending');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const generation = useRef(0);
  const selected = rows.find(row => row.id === selectedId);
  const fetchReports = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    try {
      const res = await adminApi.get('/admin/marketplace/reports', { params: { status, page, limit: 20 } });
      if (request !== generation.current) return;
      setRows(res.data.reports); setTotal(res.data.total);
      setSelectedId(current => res.data.reports.some((r: ReportCase) => r.id === current) ? current : null);
    } catch (error: any) {
      if (request === generation.current) {
        setRows([]); setTotal(0);
        setMessage(error.response?.status === 404 ? 'Listing reports are currently unavailable.' : errorText(error, 'Could not load listing reports.'));
      }
    } finally { if (request === generation.current) setLoading(false); }
  }, [page, status]);
  useEffect(() => { void fetchReports(); return () => { generation.current += 1; }; }, [fetchReports]);

  const resolve = async (action: Action) => {
    if (!selected || !note.trim() || busy) return;
    if (action !== 'dismiss_report' && !window.confirm(action === 'remove_and_suspend'
      ? 'Remove this listing and suspend this seller’s selling access? All their listings will be hidden from contact buyers.'
      : 'Remove this listing and resolve its pending complaints?')) return;
    setBusy(true); setMessage('');
    try {
      await adminApi.post(`/admin/marketplace/reports/${selected.id}/review`, { action, resolution_note: note.trim() });
      setMessage(action === 'dismiss_report' ? 'Report dismissed.' : action === 'remove_and_suspend' ? 'Listing removed and seller suspended.' : 'Listing removed.');
      setSelectedId(null); setNote('');
      if (action !== 'dismiss_report') onListingChanged?.();
      await fetchReports();
    } catch (error: any) { setMessage(errorText(error, 'Could not review this report. Refresh and retry.')); }
    finally { setBusy(false); }
  };

  return <section className="bg-white rounded-2xl p-5 border border-gray-100 space-y-4" aria-label="Listing complaints">
    <div className="flex flex-wrap gap-3 items-center justify-between">
      <h2 className="text-lg font-bold">Listing complaints ({total})</h2>
      <label>Report status <select aria-label="Report status" value={status} disabled={busy} onChange={event => { setStatus(event.target.value); setPage(1); setNote(''); setSelectedId(null); }}>
        <option value="pending">Pending</option><option value="resolved">Resolved</option><option value="all">All</option>
      </select></label>
      <button disabled={busy || loading} onClick={() => { setMessage(''); void fetchReports(); }}>Refresh reports</button>
    </div>
    {message && <p role="status">{message}</p>}
    {loading ? <p>Loading reports…</p> : rows.length === 0 ? <p>No reports in this view.</p> : <ul className="space-y-2">
      {rows.map(row => <li key={row.id}><button disabled={busy} className="text-left underline" onClick={() => { setSelectedId(row.id); setNote(''); }}>
        {row.snapshot?.title || row.current_listing?.title || 'Unavailable listing'} — {row.reason.replaceAll('_', ' ')} — {row.status}
      </button></li>)}
    </ul>}
    {selected && <div className="border rounded-xl p-4 space-y-3">
      <h3 className="font-bold">Complaint details</h3>
      <p>Reporter: {selected.reporter.name || selected.reporter.id}</p>
      <p>Buyer note: {selected.description || 'No additional details.'}</p>
      <p>Original listing: {selected.snapshot?.title || 'Snapshot unavailable'} · LKR {selected.snapshot?.price_lkr ?? '—'}</p>
      <p>{selected.snapshot?.description}</p>
      <div className="flex gap-3">{(selected.snapshot?.photos || []).filter(url => /^https?:\/\//i.test(url)).map((url, index) =>
        <a key={index} href={url} target="_blank" rel="noopener noreferrer">View photo {index + 1}</a>)}</div>
      <p>Current listing: {selected.current_listing?.title || 'Unavailable'} · {selected.current_listing?.status || 'Unavailable'}</p>
      <p>Seller: {selected.seller?.name || 'Unavailable'} {selected.seller?.is_suspended ? '(selling suspended)' : ''}</p>
      {selected.status === 'pending' ? <>
        <label className="block">Resolution note (required)<textarea aria-label="Resolution note (required)" value={note} maxLength={2000} disabled={busy} onChange={event => setNote(event.target.value)} className="block w-full border p-2 rounded" /></label>
        <div className="flex flex-wrap gap-3">{(['dismiss_report', 'remove_listing', 'remove_and_suspend'] as Action[]).map(action =>
          <button key={action} disabled={busy || !note.trim() || (action === 'remove_and_suspend' && !selected.seller)} onClick={() => { void resolve(action); }} className="border rounded px-3 py-2 disabled:opacity-50">
            {action === 'dismiss_report' ? 'Dismiss report' : action === 'remove_listing' ? 'Remove listing' : 'Remove listing + suspend seller'}
          </button>)}</div>
      </> : <p>Resolution: {selected.resolution_action} — {selected.resolution_note}</p>}
    </div>}
    <div className="flex gap-3"><button disabled={busy || loading || page === 1} onClick={() => setPage(p => p - 1)}>Previous reports</button>
      <span>Page {page}</span><button disabled={busy || loading || page * 20 >= total} onClick={() => setPage(p => p + 1)}>Next reports</button></div>
  </section>;
}
